import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, LogOut, ChevronLeft, Eye, EyeOff, Save } from 'lucide-react';
import { changePassword } from '../api';
import toast from 'react-hot-toast';

const AdminProfile = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
    if (user.role === 'agent') { navigate('/agent'); return; }
    setAdmin(user);
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.current_password || !form.new_password) {
      toast.error('Tous les champs sont requis');
      return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error('Les nouveaux mots de passe ne correspondent pas');
      return;
    }
    if (form.new_password.length < 6) {
      toast.error('Le nouveau mot de passe doit faire au moins 6 caractères');
      return;
    }
    setSaving(true);
    try {
      await changePassword({ current_password: form.current_password, new_password: form.new_password });
      toast.success('Mot de passe modifié avec succès');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    }
    setSaving(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin');
  };

  if (!admin) return null;

  return (
    <div style={{ minHeight: '100vh', background: '#f8faf8' }}>
      {/* Top bar */}
      <div style={{ background: 'white', borderBottom: '1px solid #e8e8e8', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={() => navigate('/admin/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#666', fontSize: '14px' }}>
            <ChevronLeft size={18} /> Retour
          </button>
          <h1 style={{ fontSize: '18px', fontWeight: '700', color: '#1a1a1a', margin: 0 }}>Mon Compte</h1>
        </div>
        <button onClick={handleLogout} style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
          <LogOut size={16} /> Déconnexion
        </button>
      </div>

      <div style={{ maxWidth: '500px', margin: '0 auto', padding: '24px' }}>
        {/* Info card */}
        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', marginBottom: '24px', border: '1px solid #e8e8e8' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'linear-gradient(135deg, #2E7D32, #4CAF50)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '22px', fontWeight: '700' }}>
              {admin.full_name?.charAt(0) || admin.username?.charAt(0) || 'A'}
            </div>
            <div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a1a' }}>{admin.full_name || admin.username}</div>
              <div style={{ fontSize: '13px', color: '#888', textTransform: 'capitalize' }}>{admin.role === 'admin' ? 'Administrateur' : 'Agent'}</div>
            </div>
          </div>
          <div style={{ fontSize: '13px', color: '#666' }}>
            <span style={{ fontWeight: '600' }}>Nom d'utilisateur :</span> {admin.username}
          </div>
        </div>

        {/* Change password */}
        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', border: '1px solid #e8e8e8' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a1a', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Lock size={18} color="#2E7D32" /> Changer le mot de passe
          </h2>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '6px', fontWeight: '500' }}>Mot de passe actuel</label>
              <div style={{ position: 'relative' }}>
                <input type={showCurrent ? 'text' : 'password'} value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                  style={{ width: '100%', padding: '12px 40px 12px 14px', borderRadius: '10px', border: '1.5px solid #e0e0e0', fontSize: '14px', outline: 'none' }} />
                <button type="button" onClick={() => setShowCurrent(!showCurrent)} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}>
                  {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '6px', fontWeight: '500' }}>Nouveau mot de passe</label>
              <div style={{ position: 'relative' }}>
                <input type={showNew ? 'text' : 'password'} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                  style={{ width: '100%', padding: '12px 40px 12px 14px', borderRadius: '10px', border: '1.5px solid #e0e0e0', fontSize: '14px', outline: 'none' }} />
                <button type="button" onClick={() => setShowNew(!showNew)} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}>
                  {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '6px', fontWeight: '500' }}>Confirmer le nouveau mot de passe</label>
              <input type="password" value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #e0e0e0', fontSize: '14px', outline: 'none' }} />
            </div>
            <button type="submit" disabled={saving}
              style={{ background: saving ? '#e0e0e0' : 'linear-gradient(135deg, #2E7D32, #4CAF50)', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: saving ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Save size={16} /> {saving ? 'Enregistrement...' : 'Modifier le mot de passe'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;
